import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'

export const ToastContext = createContext({
  showToast: () => {},
  dismissToast: () => {},
})

const buildId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])
  const timeoutsRef = useRef(new Map())

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
    const timeout = timeoutsRef.current.get(id)
    if (timeout) {
      clearTimeout(timeout)
      timeoutsRef.current.delete(id)
    }
  }, [])

  const showToast = useCallback((message, options = {}) => {
    if (!message) return null
    const id = buildId()
    const nextToast = {
      id,
      message,
      title: options.title || null,
      tone: options.tone || 'neutral',
      duration: typeof options.duration === 'number' ? options.duration : 6000,
    }
    setToasts((prev) => [...prev, nextToast])

    if (nextToast.duration > 0) {
      const timeout = setTimeout(() => {
        dismissToast(id)
      }, nextToast.duration)
      timeoutsRef.current.set(id, timeout)
    }

    return id
  }, [dismissToast])

  useEffect(() => {
    const timeouts = timeoutsRef.current
    return () => {
      timeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId)
      })
      timeouts.clear()
    }
  }, [])

  const contextValue = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="toast-stack" role="region" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`} role="status">
            <span className="toast__indicator" aria-hidden="true" />
            <div className="toast__content">
              {toast.title && <p className="toast__title">{toast.title}</p>}
              <p className="toast__message">{toast.message}</p>
            </div>
            <button
              type="button"
              className="toast__close"
              onClick={() => dismissToast(toast.id)}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
